import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

export const ProjectFiles = ({ projectId, user }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  const fetchFiles = async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/files`);
      setFiles(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchFiles(); }, [projectId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File is larger than 15MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/projects/${projectId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      toast.success(`${file.name} uploaded`);
      fetchFiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDownload = async (f) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/files/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleView = async (f) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/files/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Could not open file');
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete "${f.filename}" for everyone?`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/files/${f.id}`);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      toast.success('File deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete file');
    }
  };

  return (
    <div className="glass-card p-4 sm:p-6 mb-6" data-testid="project-files-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-2 bg-red-500/20 rounded-lg flex-shrink-0">
            <FileText className="w-5 h-5 text-red-400" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Project Files</h2>
          {files.length > 0 && (
            <span className="text-sm text-slate-400">({files.length})</span>
          )}
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={handleUpload}
            data-testid="project-file-input"
          />
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full sm:w-auto bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white min-h-[44px]"
            data-testid="upload-project-file-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload PDF
          </Button>
        </div>
      </div>

      {files.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No files yet — upload plans, permits or specs the crew needs</p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between gap-3 bg-slate-800/50 border border-slate-700 rounded-lg p-3"
              data-testid={`project-file-${f.id}`}
            >
              <button onClick={() => handleView(f)} className="flex items-center gap-3 min-w-0 text-left flex-1">
                <FileText className="w-7 h-7 text-red-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{f.filename}</p>
                  <p className="text-xs text-slate-400">
                    {f.uploaded_by_name} · {new Date(f.created_at).toLocaleDateString('en-CA')} · {formatSize(f.file_size)}
                  </p>
                </div>
              </button>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleDownload(f)}
                  className="p-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700"
                  title="Download"
                  data-testid={`download-file-${f.id}`}
                >
                  <Download className="w-4 h-4" />
                </button>
                {(isAdmin || f.uploaded_by === user?.id) && (
                  <button
                    onClick={() => handleDelete(f)}
                    className="p-2.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                    title="Delete"
                    data-testid={`delete-file-${f.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
